import React, { memo } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Stack,
    Chip,
    alpha,
    useTheme,
    Divider,
} from '@mui/material';
import { OpenInNew, TrendingUp } from '@mui/icons-material';
import { useTheme as useCustomTheme } from '../../theme/ThemeProvider';
import { useAnalytics } from '../Analytics';

// Affiliate product data
const affiliateProducts = {
    originality: {
        name: 'Originality.ai',
        description: 'Professional AI content detection and plagiarism checker',
        link: 'https://originality.ai/?via=ts-ws',
        category: 'AI Detection',
        color: '#ef4444',
        badge: 'Recommended',
    },
};

// Always show Originality.ai since it's the only one we use now
const products = [affiliateProducts.originality];

const AffiliateBox = memo(({ context }) => {
    const theme = useTheme();
    const { darkMode } = useCustomTheme();
    const { trackAffiliateClick } = useAnalytics();

    return (
        <Paper
            elevation={0}
            sx={{
                p: 4,
                borderRadius: 4,
                border: `1px solid ${theme.palette.divider}`,
                background: darkMode
                    ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(6, 182, 212, 0.05) 100%)'
                    : 'linear-gradient(135deg, rgba(99, 102, 241, 0.03) 0%, rgba(6, 182, 212, 0.03) 100%)',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TrendingUp sx={{ color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Recommended Tool
                </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Take your {context?.toLowerCase() || 'text analysis'} to the next level with this professional tool.
            </Typography>

            <Stack spacing={2}>
                {products.map((product, index) => (
                    <Box key={product.name}>
                        {index > 0 && <Divider sx={{ my: 2 }} />}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 2,
                                flexDirection: { xs: 'column', sm: 'row' },
                            }}
                        >
                            <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                        {product.name}
                                    </Typography>
                                    {product.badge && (
                                        <Chip
                                            label={product.badge}
                                            size="small"
                                            sx={{
                                                backgroundColor: alpha(product.color, 0.1),
                                                color: product.color,
                                                fontSize: '0.7rem',
                                                height: 20,
                                            }}
                                        />
                                    )}
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                    {product.description}
                                </Typography>
                            </Box>
                            <Button
                                variant="outlined"
                                size="small"
                                href={product.link}
                                target="_blank"
                                rel="noopener noreferrer sponsored"
                                endIcon={<OpenInNew sx={{ fontSize: 14 }} />}
                                sx={{
                                    borderColor: product.color,
                                    color: product.color,
                                    whiteSpace: 'nowrap',
                                    '&:hover': {
                                        borderColor: product.color,
                                        backgroundColor: alpha(product.color, 0.1),
                                    },
                                }}
                                onClick={() => trackAffiliateClick(product.name, context || 'blog_post')}
                            >
                                Try Free
                            </Button>
                        </Box>
                    </Box>
                ))}
            </Stack>

            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 3, textAlign: 'center' }}
            >
                We may earn a commission if you click this link and make a purchase.
            </Typography>
        </Paper >
    );
});

export default AffiliateBox;
